import React, { useState } from 'react';
import { CardElement, useStripe as useStripeJs, useElements } from '@stripe/react-stripe-js';
import { useStripe } from '../../contexts/StripeContext';
import { Loader2, AlertTriangle } from 'lucide-react';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const PaymentForm = ({ 
  amount, 
  eventId, 
  conventionOwnerId, 
  registrationData,
  onSuccess,
  onError
}) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  
  // Get Stripe context and elements
  const { createPayment, stripeLoaded, error: stripeContextError } = useStripe();
  const stripeJs = useStripeJs();
  const elements = useElements();

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripeJs || !elements) {
      // Stripe.js has not loaded yet
      setError('Stripe payment system is not available at the moment. Please try again later.');
      return;
    }
    
    // Validate inputs
    if (!amount || !eventId || !conventionOwnerId) {
      setError('Missing required payment information');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      
      // Create a payment intent
      const paymentIntent = await createPayment({
        amount: Math.round(amount * 100), // Convert to cents
        eventId,
        conventionOwnerId,
        registrationData
      });
      
      if (!paymentIntent || !paymentIntent.clientSecret) {
        throw new Error('Failed to create payment intent');
      }
      
      // Confirm the payment with the card element
      const result = await stripeJs.confirmCardPayment(paymentIntent.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: registrationData?.name || '',
            email: registrationData?.email || '',
          },
        },
      });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
      // Payment succeeded
      setSucceeded(true);
      setProcessing(false);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(result.paymentIntent);
      }
      
    } catch (err) {
      setError(err.message || 'An error occurred while processing your payment');
      setProcessing(false);
      
      // Call error callback
      if (onError) {
        onError(err);
      }
    }
  };

  // If Stripe failed to load entirely, show error message
  if (!stripeLoaded) {
    return (
      <div className="max-w-md mx-auto p-6 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/30 dark:border-red-800">
        <div className="flex items-center mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mr-2" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300">Payment Not Available</h3>
        </div>
        <p className="text-red-700 dark:text-red-300 mb-3">
          The payment system is currently unavailable. This could be due to:
        </p>
        <ul className="list-disc list-inside text-red-700 dark:text-red-300 mb-4 ml-2 space-y-1">
          <li>Temporary service disruption</li>
          <li>Network connectivity issues</li>
          <li>Browser compatibility problems</li>
        </ul>
        <p className="text-red-700 dark:text-red-300 font-medium">
          Please try again later or contact support if the problem persists.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Display any context-level Stripe errors */}
        {stripeContextError && (
          <div className="p-3 mb-4 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-300">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{stripeContextError}</span>
            </div>
          </div>
        )}
      
        <div className="p-4 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Card Details
          </label>
          <CardElement options={CARD_ELEMENT_OPTIONS} className="p-3 border border-gray-300 dark:border-gray-600 rounded-md" />
        </div>
        
        {/* Amount display */}
        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <span className="text-gray-700 dark:text-gray-300">Total Amount:</span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            ${(amount || 0).toFixed(2)}
          </span>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
            {error}
          </div>
        )}
        
        {/* Success message */}
        {succeeded && (
          <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-md dark:bg-green-900/30 dark:border-green-800 dark:text-green-300">
            Payment successful! Thank you for your purchase.
          </div>
        )}
        
        {/* Submit button */}
        <button
          type="submit"
          disabled={processing || succeeded || !stripeJs}
          className={`w-full py-3 px-4 rounded-md text-white font-medium transition-colors ${
            processing || succeeded || !stripeJs
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {processing ? (
            <span className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </span>
          ) : succeeded ? (
            'Payment Successful'
          ) : (
            'Pay Now'
          )}
        </button>
      </form>
    </div>
  );
};

export default PaymentForm; 